import { useCallback, useMemo } from 'react';
import { useBudgetLineContext } from './context';
import { ACTIONS } from './reducer';
import { apiCaller, APIError, createSearchCriteria, createPageable } from 'utils/axios';
import { API_ENDPOINTS } from 'hooks/api/apiEndpoints';
import Swal from 'sweetalert2';

export const useBudgetLine = () => {
    const [state, dispatch] = useBudgetLineContext();

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const handleError = useCallback(
        (error) => {
            let errorMessage = 'An unexpected error occurred';
            let errors = null;

            if (error instanceof APIError) {
                errorMessage = error.message;
                errors = error.errors;
            }

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });

            dispatch({ type: ACTIONS.SET_ERROR, payload: { message: errorMessage, errors } });
        },
        [dispatch]
    );

    const setFilter = useCallback(
        (filterData) => {
            dispatch({ type: ACTIONS.SET_FILTER, payload: filterData });
        },
        [dispatch]
    );

    const setSort = useCallback(
        (sortData) => {
            dispatch({ type: ACTIONS.SET_SORT, payload: sortData });
        },
        [dispatch]
    );

    const setPage = useCallback(
        (pageData) => {
            dispatch({ type: ACTIONS.SET_PAGE, payload: pageData });
        },
        [dispatch]
    );

    const fetchBudgetLines = useCallback(async () => {
        try {
            setLoading(true);
            const { filter, pagination, sort } = state;

            const searchCriteria = Object.entries(filter)
                .filter(([_, value]) => value !== null && value !== undefined)
                .map(([key, value]) =>
                    createSearchCriteria({
                        key,
                        value,
                        operation: 'EQUAL'
                    })
                );

            const pageable = createPageable({
                page: pagination.page,
                size: pagination.size,
                sort: [{ field: sort.field, direction: sort.direction }]
            });

            const data = await apiCaller.search(API_ENDPOINTS.BUDGET_LINES, searchCriteria, pageable);
            dispatch({ type: ACTIONS.SET_BUDGET_LINES, payload: data });
            return data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, dispatch, state]);

    const searchBudgetLines = useCallback(
        async (searchParams = {}, pageParams = {}) => {
            setFilter(searchParams);
            setPage(pageParams);
            return fetchBudgetLines();
        },
        [setFilter, setPage, fetchBudgetLines]
    );

    const createBudgetLine = useCallback(
        async (budgetLineData) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(API_ENDPOINTS.BUDGET_LINES, budgetLineData);
                dispatch({ type: ACTIONS.ADD_BUDGET_LINE, payload: data });

                showSuccessMessage('Budget line created successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateBudgetLine = useCallback(
        async (id, budgetLineData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.BUDGET_LINES}/${id}`, budgetLineData);
                dispatch({ type: ACTIONS.UPDATE_BUDGET_LINE, payload: { id, data } });

                showSuccessMessage('Budget line updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const deleteBudgetLine = useCallback(
        async (id) => {
            try {
                setLoading(true);
                await apiCaller.delete(`${API_ENDPOINTS.BUDGET_LINES}/${id}`);
                dispatch({ type: ACTIONS.DELETE_BUDGET_LINE, payload: id });

                showSuccessMessage('Budget line deleted successfully');
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const getBudgetLineById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.BUDGET_LINES}/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_BUDGET_LINE, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, [dispatch]);

    const listBudgetLineCodes = useCallback(
        async (page = 0, size = 10) => {
            try {
                setLoading(true);
                const data = await apiCaller.list(API_ENDPOINTS.BUDGET_LINES, page, size);
                dispatch({ type: ACTIONS.SET_BUDGET_LINES, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    return useMemo(
        () => ({
            budgetLines: state.budgetLines,
            selectedBudgetLine: state.selectedBudgetLine,
            loading: state.loading,
            error: state.error,
            filter: state.filter,
            pagination: state.pagination,
            sort: state.sort,
            lastUpdated: state.lastUpdated,

            setFilter,
            setSort,
            setPage,
            fetchBudgetLines,
            searchBudgetLines,

            createBudgetLine,
            updateBudgetLine,
            deleteBudgetLine,
            getBudgetLineById,

            clearError,
            listBudgetLineCodes
        }),
        [
            state,
            setFilter,
            setSort,
            setPage,
            fetchBudgetLines,
            searchBudgetLines,
            createBudgetLine,
            updateBudgetLine,
            deleteBudgetLine,
            getBudgetLineById,
            clearError,
            listBudgetLineCodes
        ]
    );
};

export default useBudgetLine;
